import { z } from 'zod';
import {
  businessSchema,
  heroAppIdSchema,
  heroPlatformSchema,
} from './app-registry.schema';
import { conversationChannelTypeSchema } from './channel.schema';
import { creatorSchema } from './creator.schema';
import { dateSchema, objectIdSchema } from '../validation';

export const docStatusSchema = z.enum(['active', 'deleted']);
export type DocStatus = z.infer<typeof docStatusSchema>;

export const docEnvSchema = z.enum(['development', 'production', 'staging', 'test']);
export type DocEnv = z.infer<typeof docEnvSchema>;

/**
 * The envelope every document carries: when, who, and — as of the app
 * registry — *from where*.
 *
 * The provenance fields are flat rather than nested under an `origin`
 * object, matching `lastSeenOnlineSchema` (`{ at, app, platform }`) and
 * the `env` stamp that was already here. Same vocabulary as the
 * `x-hero-app` / `x-hero-platform` headers and
 * `User.meta.presence.apps[]`, so "which surface did this come from" is
 * one question with one answer shape across presence, sessions,
 * conversations, and documents.
 *
 * All of them are optional, and absence is meaningful rather than a gap
 * to paper over: a cron writing an invoice genuinely had no app and no
 * platform. Readers must handle `undefined` explicitly — there is no
 * `?? 'hero'` default anywhere, because a silent default is how a
 * Wathiqa Plus order gets counted as Hero revenue.
 *
 * Stamped once, in `DBCrudService.newDoc`, from the CLS context — the
 * same slots `AnalyticsService` reads to enrich events, so provenance and
 * analytics can never disagree about which surface a turn was on.
 */
export const docSchema = z.object({
  createdAt: dateSchema,
  updatedAt: dateSchema,
  deletedAt: dateSchema.optional(),
  status: docStatusSchema.default('active'),
  creator: creatorSchema,
  /** Stamped on non-production creates to identify the environment that created this document. */
  env: docEnvSchema.optional(),
  /**
   * Which Hero app this was created from. Absent means no client app was
   * involved — a cron, a webhook, a migration.
   *
   * In-app AI chat inside the dashboard stamps `dashboard`, not
   * `hero_agent`: this field answers "what did the human have in front of
   * them". {@link isAgent} is what marks AI authorship.
   */
  app: heroAppIdSchema.optional(),
  /**
   * Runtime behind {@link app} (`web` | `ios` | `android` | `whatsapp` |
   * `phone`). WhatsApp agent turns stamp `whatsapp`, not `unknown`.
   * Absent with {@link app} on crons / webhooks.
   */
  platform: heroPlatformSchema.optional(),
  /**
   * Brand of {@link app}. Derived via `appBusiness(app)`. Absent when
   * `app` is absent (cron). Never default to `hero`.
   */
  business: businessSchema.optional(),
  /**
   * How the write reached Hero — dashboard/mobile UI (`in_app`), WhatsApp,
   * etc. Same vocabulary as {@link Conversation}. Absent for crons and
   * webhooks with no client app in scope.
   */
  channel: conversationChannelTypeSchema.optional(),
  /**
   * Written by the AI agent rather than a person driving a UI.
   *
   * Deliberately independent of the registry's `isAgentSurface`: that
   * says "this app is only ever the agent", this says "the AI wrote this
   * row" — true for dashboard in-app chat, where the registry flag is
   * false.
   */
  isAgent: z.boolean().optional(),
  /** The thread this was created in, for tracing a document back to the conversation. */
  conversationId: objectIdSchema.optional(),
});
export type Doc = z.infer<typeof docSchema>;
